import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Modal from './Modal';

interface Item {
  id: number;
  label: string;
}

interface Section {
  id: number;
  title: string;
  items: Item[];
}

interface StructureEditorProps {
  token: string;
}

const isProd = process.env.NODE_ENV === 'production';
const BASE_URL = isProd ? '/api/admin' : `http://${window.location.hostname}:4658/api/admin`;

const StructureEditor: React.FC<StructureEditorProps> = ({ token }) => {
  const [sections, setSections] = useState<Section[]>([]);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newItemLabel, setNewItemLabel] = useState<{ [key: number]: string }>({});
  const [error, setError] = useState('');

  // Editing state
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  // Modal State
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const fetchStructure = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/structure`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
    } catch (err) {
      setError('Struktur konnte nicht geladen werden.');
    }
  }, [token]);

  useEffect(() => {
    fetchStructure();
  }, [fetchStructure]);

  const handleAddSection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSectionTitle.trim()) return;
    try {
      await axios.post(`${BASE_URL}/sections`, 
        { title: newSectionTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewSectionTitle('');
      fetchStructure();
    } catch (err) {
      setError('Fehler beim Hinzufügen des Bereichs');
    }
  };

  const handleUpdateSectionTitle = async (id: number) => {
    if (!editValue.trim()) {
      setEditingSectionId(null);
      return;
    }
    try {
      await axios.put(`${BASE_URL}/sections/${id}`, 
        { title: editValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingSectionId(null);
      fetchStructure();
    } catch (err) {
      setError('Fehler beim Aktualisieren des Titels');
    }
  };

  const handleMoveSection = async (index: number, direction: 'up' | 'down') => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSections.length) return;

    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    const orders = newSections.map((s, i) => ({ id: s.id, order_index: i + 1 }));
    try {
      await axios.post(`${BASE_URL}/reorder-sections`, { orders }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(newSections);
    } catch (err) {
      setError('Fehler beim Verschieben');
    }
  };

  const handleMoveItem = async (sectionIndex: number, itemIndex: number, direction: 'up' | 'down') => {
    const section = sections[sectionIndex];
    const newItems = [...section.items];
    const targetIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    [newItems[itemIndex], newItems[targetIndex]] = [newItems[targetIndex], newItems[itemIndex]];
    
    const orders = newItems.map((item, i) => ({ id: item.id, order_index: i + 1 }));
    try {
      await axios.post(`${BASE_URL}/reorder-templates`, { orders }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newSections = [...sections];
      newSections[sectionIndex] = { ...section, items: newItems };
      setSections(newSections);
    } catch (err) {
      setError('Fehler beim Verschieben');
    }
  };

  const handleUpdateItemLabel = async (id: number) => {
    if (!editValue.trim()) {
      setEditingItemId(null);
      return;
    }
    try {
      await axios.put(`${BASE_URL}/templates/${id}`, 
        { label: editValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingItemId(null);
      fetchStructure();
    } catch (err) {
      setError('Fehler beim Aktualisieren der Übung');
    }
  };

  const openDeleteSectionModal = (id: number, title: string) => {
    setModalConfig({
      isOpen: true,
      title: 'Bereich löschen?',
      message: `Bist du sicher, dass du den Bereich "${title}" und alle darin enthaltenen Übungen löschen möchtest?`,
      onConfirm: async () => {
        try {
          await axios.delete(`${BASE_URL}/sections/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setModalConfig(prev => ({ ...prev, isOpen: false }));
          fetchStructure();
        } catch (err) {
          setError('Fehler beim Löschen');
          setModalConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const openDeleteItemModal = (id: number, label: string) => {
    setModalConfig({
      isOpen: true,
      title: 'Übung löschen?',
      message: `Möchtest du die Übung "${label}" wirklich löschen?`,
      onConfirm: async () => {
        try {
          await axios.delete(`${BASE_URL}/templates/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setModalConfig(prev => ({ ...prev, isOpen: false }));
          fetchStructure();
        } catch (err) {
          setError('Fehler beim Löschen');
          setModalConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleAddItem = async (sectionId: number) => {
    const label = newItemLabel[sectionId];
    if (!label || !label.trim()) return;
    try {
      await axios.post(`${BASE_URL}/templates`, 
        { sectionId, label },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewItemLabel({ ...newItemLabel, [sectionId]: '' });
      fetchStructure();
    } catch (err) {
      setError('Fehler beim Hinzufügen der Übung');
    }
  };

  return (
    <div className="structure-editor" style={{ padding: '20px' }}>
      <h2>Karten-Aufbau bearbeiten</h2>
      {error && <div className="error-toast" style={{ position: 'static', marginBottom: '10px' }}>{error}</div>}

      <div className="dashboard-card" style={{ marginBottom: '30px', cursor: 'default' }}>
        <h3>Neuen Bereich hinzufügen</h3>
        <form onSubmit={handleAddSection} style={{ display: 'flex', gap: '10px', width: '100%' }}>
          <input 
            className="input-field" 
            style={{ marginBottom: 0 }}
            placeholder="Titel des Bereichs" 
            value={newSectionTitle} 
            onChange={(e) => setNewSectionTitle(e.target.value)} 
          />
          <button type="submit" className="btn btn-primary">Hinzufügen</button>
        </form>
      </div>

      {sections.map((section, sIdx) => (
        <div key={section.id} className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button className="btn" style={{ padding: '5px 10px', fontSize: '12px' }} onClick={() => handleMoveSection(sIdx, 'up')}>▲</button>
                <button className="btn" style={{ padding: '5px 10px', fontSize: '12px' }} onClick={() => handleMoveSection(sIdx, 'down')}>▼</button>
              </div>
              {editingSectionId === section.id ? (
                <input 
                  className="input-field"
                  style={{ marginBottom: 0, flex: 1 }}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={() => handleUpdateSectionTitle(section.id)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateSectionTitle(section.id)}
                  autoFocus
                />
              ) : (
                <span 
                  style={{ cursor: 'pointer', flex: 1 }} 
                  onClick={() => { setEditingSectionId(section.id); setEditValue(section.title); }}
                >
                  {section.title}
                </span>
              )}
            </div>
            <button className="btn btn-danger btn-small" onClick={() => openDeleteSectionModal(section.id, section.title)}>Bereich löschen</button>
          </div>
          <div className="card-content">
            {section.items.map((item, iIdx) => (
              <div key={item.id} className="item-row" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button className="btn" style={{ padding: '2px 8px', fontSize: '10px' }} onClick={() => handleMoveItem(sIdx, iIdx, 'up')}>▲</button>
                    <button className="btn" style={{ padding: '2px 8px', fontSize: '10px' }} onClick={() => handleMoveItem(sIdx, iIdx, 'down')}>▼</button>
                  </div>
                  {editingItemId === item.id ? (
                    <input 
                      className="input-field"
                      style={{ marginBottom: 0, flex: 1 }}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleUpdateItemLabel(item.id)}
                      onKeyDown={(e) => e.key === 'Enter' && handleUpdateItemLabel(item.id)}
                      autoFocus
                    />
                  ) : (
                    <span 
                      style={{ cursor: 'pointer', flex: 1 }} 
                      onClick={() => { setEditingItemId(item.id); setEditValue(item.label); }}
                    >
                      {item.label}
                    </span>
                  )}
                </div>
                <button className="btn btn-danger btn-small" onClick={() => openDeleteItemModal(item.id, item.label)}>X</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <input 
                className="input-field" 
                style={{ marginBottom: 0 }} 
                placeholder="Neue Übung..." 
                value={newItemLabel[section.id] || ''} 
                onChange={(e) => setNewItemLabel({ ...newItemLabel, [section.id]: e.target.value })} 
              />
              <button className="btn btn-secondary" onClick={() => handleAddItem(section.id)}>Hinzufügen</button>
            </div>
          </div>
        </div>
      ))}

      {modalConfig.isOpen && (
        <Modal 
          title={modalConfig.title}
          message={modalConfig.message}
          onConfirm={modalConfig.onConfirm}
          onCancel={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
        />
      )}
    </div>
  );
};

export default StructureEditor;
